'use client';

import Image from 'next/image';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { caseAPI, settingAPI } from '@/lib/api';
import { getImageUrl } from '@/lib/utils';

interface CaseGalleryProps {
  limit?: number;
}

export default function CaseGallery({ limit = 4 }: CaseGalleryProps) {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());

  // 优先从设置中获取图片
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingAPI.getAll(),
    staleTime: 0, // 设置为0，确保每次都获取最新数据
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // 如果没有设置图片，则从案例列表获取
  const { data: casesData, isLoading } = useQuery({
    queryKey: ['cases-gallery', limit],
    queryFn: () =>
      caseAPI.getList({
        status: 'published',
        limit,
      }),
    enabled: !settingsData?.cooperation_case_images, // 如果没有设置图片才获取案例
  });

  // 获取图片列表（优先使用设置中的图片）
  const getImages = () => {
    // settingAPI.getAll() 现在直接返回 Settings 对象
    const settings = settingsData || {};
    
    if (settings.cooperation_case_images) {
      try {
        let images: string[] = [];
        
        // 处理不同的数据格式
        if (typeof settings.cooperation_case_images === 'string') {
          // 如果是字符串，尝试解析
          try {
            const parsed = JSON.parse(settings.cooperation_case_images);
            images = Array.isArray(parsed) ? parsed : [parsed];
          } catch (parseError) {
            // 如果不是有效的 JSON，尝试作为单个 URL 处理
            images = [settings.cooperation_case_images];
          }
        } else if (Array.isArray(settings.cooperation_case_images)) {
          // 如果已经是数组，直接使用
          images = settings.cooperation_case_images;
        } else {
          // 其他类型，尝试转换为数组
          images = [String(settings.cooperation_case_images)];
        }
        
        if (Array.isArray(images) && images.length > 0) {
          return images
            .filter(url => url && typeof url === 'string' && url.trim().length > 0) // 过滤空值
            .slice(0, limit)
            .map((url: string, index: number) => {
              // 确保 URL 格式正确
              let imageUrl = url.trim();
              if (!imageUrl.startsWith('/') && !imageUrl.startsWith('http')) {
                imageUrl = '/' + imageUrl;
              }
              
              return {
                id: `setting-${index}`,
                featuredImage: imageUrl,
                title: 'Case Image',
              };
            });
        }
      } catch (e) {
        console.error('Failed to parse cooperation_case_images:', e);
      }
    }
    
    // 如果没有设置图片，使用案例列表
    return casesData?.data || [];
  };

  const images = getImages();
  
  // 处理图片加载
  const handleImageLoad = (imageUrl: string) => {
    setLoadedImages(prev => new Set(prev).add(imageUrl));
  };
  
  // 预加载图片
  useEffect(() => {
    if (images && images.length > 0) {
      images.forEach((item: any) => {
        const imageUrl = getImageUrl(item.featuredImage);
        if (!loadedImages.has(imageUrl)) {
          const img = new window.Image();
          img.onload = () => handleImageLoad(imageUrl);
          img.onerror = () => handleImageLoad(imageUrl); // 即使加载失败也标记为已加载
          img.src = imageUrl;
        }
      });
    }
  }, [images, loadedImages]);
  
  // 如果正在加载且没有设置数据，显示加载状态
  if ((isLoading && !settingsData) || (settingsData === undefined && casesData === undefined)) {
    return (
      <div className="case-gallery">
        <div className="elementor-gallery__container">
          {[...Array(limit)].map((_, i) => (
            <div key={i} className="e-gallery-item bg-gray-200 animate-pulse aspect-[3/2]" />
          ))}
        </div>
      </div>
    );
  }
  
  if (images.length === 0) {
    console.log('No images to display');
    return (
      <div className="case-gallery">
        <p className="text-text-light">暂无案例图片</p>
        <p>Settings data: {JSON.stringify(settingsData?.cooperation_case_images)}</p>
      </div>
    );
  }
  
  console.log('About to render', images.length, 'images');

  console.log('Rendering images, count:', images.length);
  console.log('Loaded images set:', Array.from(loadedImages));

  return (
    <div 
      className="case-gallery" 
      style={{ 
        width: '100%', 
        display: 'block',
        minWidth: '300px',
        boxSizing: 'border-box',
      }}
    >
      <div 
        className="elementor-gallery__container" 
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '22px',
          width: '100%',
          minWidth: '300px',
          boxSizing: 'border-box',
        }}
      >
        {images.map((item: any, index: number) => {
          const imageUrl = getImageUrl(item.featuredImage);
          const isLoaded = loadedImages.has(imageUrl);

          return (
            <a
              key={item.id || `image-${index}`}
              className="e-gallery-item elementor-gallery-item elementor-animated-content"
              href={imageUrl}
              onClick={(e) => {
                e.preventDefault();
                setSelectedImage(imageUrl);
              }}
              style={{
                display: 'block',
                position: 'relative',
                width: '100%',
                paddingBottom: '66.666%',
                height: 0,
                overflow: 'hidden',
                backgroundColor: '#f0f0f0',
              }}
            >
              <img
                src={imageUrl}
                alt={item.title || 'Case Image'}
                className={`e-gallery-image elementor-gallery-item__image elementor-animated-item--grow ${isLoaded ? 'e-gallery-image-loaded' : ''}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  opacity: 1,
                }}
                onLoad={() => handleImageLoad(imageUrl)}
                onError={() => handleImageLoad(imageUrl)}
              />
            </a>
          );
        })}
      </div>

      {/* 图片预览模态框 */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-6xl max-h-full">
            <Image
              src={selectedImage}
              alt="Case preview"
              width={1200}
              height={800}
              className="max-w-full max-h-[90vh] object-contain"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
